import Button from '@UI/New/Button'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { ArrowLeftOutlined, CheckOutlined, SendOutlined } from '@ant-design/icons'
import type {
  CrmChatProviderKey,
  CrmLeadIntegrationChatTab,
} from '@/api/types/crm/crmLeadFunnels.types'
import { CRM_INTEGRATION_CHAT_PROVIDERS } from '@pages/Admin/CRM/components/PhoneIntegrationButtons/integrations'
import { formatVkUnreadCountBadge } from '@pages/Admin/CRM/utils/leadFields'
import {
  WazzupChannelTypeMark,
  formatWazzupChannelAriaLabel,
} from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/WazzupChannelTypeMark'
import { WazzupSourceBadge } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/WazzupSourceBadge'
import { CompressIcon, IcoRemove } from '@/utils/icons'
import { FC, KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { useCrmIntegrationChatData } from '../../hooks/useCrmIntegrationChatData'
import { useCrmIntegrationConversationResolve } from '../../hooks/useCrmIntegrationConversationResolve'
import { useCrmIntegrationMessagesScroll } from '../../hooks/useCrmIntegrationMessagesScroll'
import {
  getCrmIntegrationCommunityTabLabel,
  getCrmIntegrationCommunityTabsAriaLabel,
} from '../../lib/crmIntegrationCommunityTabLabel'
import { isCrmIntegrationOutgoingAttachmentsEnabled } from '../../lib/crmChatProviderKey'
import CrmIntegrationTextChatComposer from './CrmIntegrationTextChatComposer'
import CrmIntegrationTextChatMessageList from './CrmIntegrationTextChatMessageList'
import CrmIntegrationVoiceRecorder, {
  type CrmIntegrationVoiceRecorderHandle,
} from './CrmIntegrationVoiceRecorder'
import classes from '../../styles/crmIntegrationChat.module.css'

const EMPTY_COMMUNITY_TABS: CrmLeadIntegrationChatTab[] = []

type CrmIntegrationTextChatProps = {
  provider: CrmChatProviderKey
  leadId: number
  conversationId?: number | null
  communityTabs?: CrmLeadIntegrationChatTab[]
  variant?: 'modal' | 'dock' | 'page'
  enabled?: boolean
  onDismiss?: () => void
  onCollapse?: () => void
  onBack?: () => void
  /** Канал conversation удалён по WS — закрыть UI. */
  onChannelDeleted?: () => void
  onConversationIdChange?: (conversationId: number) => void
  onMarkReadSuccess?: (leadId: number, conversationId?: number) => void
}

const getInitials = (label: string): string => {
  const parts = label.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

const formatUnreadBadgeCount = (count: number): string =>
  count > 99 ? '99+' : String(count)

const getAllReadBadgeTooltip = (provider: CrmChatProviderKey): ReactNode => {
  if (provider === 'telegram') {
    return (
      <>
        В Telegram входящие от клиента боту на стороне мессенджера часто уже
        считаются прочитанными.
        <br />
        В CRM непрочитанные сбрасываются только по кнопке «Прочитать все».
      </>
    )
  }

  if (provider === 'max') {
    return (
      <>
        В MAX в чате с ботом нет индикации прочитанности сообщений.
        <br />
        В CRM непрочитанные сбрасываются только по кнопке «Прочитать все».
      </>
    )
  }

  if (provider === 'wazzup') {
    return (
      <>В CRM непрочитанные по Wazzup сбрасываются только по кнопке «Прочитать все».</>
    )
  }

  return null
}

const CrmIntegrationTextChat: FC<CrmIntegrationTextChatProps> = ({
  provider,
  leadId,
  conversationId = null,
  communityTabs = EMPTY_COMMUNITY_TABS,
  variant = 'dock',
  enabled = true,
  onDismiss,
  onCollapse,
  onBack,
  onChannelDeleted,
  onConversationIdChange,
  onMarkReadSuccess,
}) => {
  const [draft, setDraft] = useState('')
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [isVoiceRecordingMode, setIsVoiceRecordingMode] = useState(false)
  const [voiceRecorderState, setVoiceRecorderState] = useState({
    isRecording: false,
    hasAudio: false,
    isVoiceReady: false,
  })
  const voiceRecorderRef = useRef<CrmIntegrationVoiceRecorderHandle | null>(null)
  const messagesViewportRef = useRef<HTMLDivElement | null>(null)

  const handleChannelDeleted = useCallback(() => {
    if (onChannelDeleted) {
      onChannelDeleted()
      return
    }
    onBack?.()
  }, [onBack, onChannelDeleted])

  const {
    tabs: resolvedTabs,
    conversationId: resolvedConversationId,
    setConversationId,
    setTabUnreadCount,
    isResolving,
  } = useCrmIntegrationConversationResolve({
    leadId,
    provider,
    enabled,
    initialTabs: communityTabs,
    initialConversationId: conversationId,
  })

  const handleSelectConversation = (nextConversationId: number) => {
    setConversationId(nextConversationId)
    onConversationIdChange?.(nextConversationId)
  }

  const providerMeta = CRM_INTEGRATION_CHAT_PROVIDERS.find(
    (item) => item.provider === provider,
  )
  const ProviderIcon = providerMeta?.icon

  const {
    status,
    conversationTitle,
    unreadCount,
    externalChatType,
    hasConversation,
    messages,
    errorMessage,
    isSending,
    sendMessage,
    sendVoiceMessage,
    retrySendMessage,
    loadOlderMessages,
    isLoadingOlder,
    hasMoreOlder,
    markDialogRead,
    isMarkingRead,
    loadMessages,
    deleteMessage,
    isDeletingMessageId,
  } = useCrmIntegrationChatData(leadId, {
    provider,
    conversationId: resolvedConversationId,
    enabled: enabled && resolvedConversationId != null,
    scrollContainerRef: messagesViewportRef,
    onMarkReadSuccess,
    onChannelDeleted:
      onChannelDeleted != null || onBack != null
        ? handleChannelDeleted
        : undefined,
  })

  const wazzupChannelType = useMemo(() => {
    if (provider !== 'wazzup') {
      return null
    }
    if (externalChatType?.trim()) {
      return externalChatType
    }
    const activeTab = resolvedTabs.find(
      (tab) => Number(tab.conversation_id) === Number(resolvedConversationId),
    )
    return activeTab?.external_chat_type ?? null
  }, [externalChatType, provider, resolvedConversationId, resolvedTabs])

  useEffect(() => {
    setAttachmentFile(null)
    setIsVoiceRecordingMode(false)
  }, [wazzupChannelType, resolvedConversationId])

  // Вкладка сообщества/бота: unread как у активного conversation (WS / mark-read).
  useEffect(() => {
    if (resolvedConversationId == null) {
      return
    }
    setTabUnreadCount(resolvedConversationId, unreadCount)
  }, [resolvedConversationId, setTabUnreadCount, unreadCount])

  const isConversationMissing =
    !isResolving && resolvedConversationId == null
  const messagesStatus = isResolving
    ? 'loading'
    : isConversationMissing
      ? 'error'
      : status
  const messagesErrorText = isConversationMissing
    ? 'Диалог не найден для этого лида'
    : errorMessage

  const {
    messagesListRef,
    handleMessagesViewportScroll,
    enableStickToBottom,
    scrollToBottom,
  } = useCrmIntegrationMessagesScroll({
    conversationId: resolvedConversationId,
    enabled,
    status,
    messages,
    messagesViewportRef,
  })

  const isSendBlocked = false
  const outgoingAttachmentsEnabled =
    isCrmIntegrationOutgoingAttachmentsEnabled(provider)
  const canSend =
    messagesStatus === 'ready' &&
    !isSendBlocked &&
    !isSending &&
    (draft.trim().length > 0 ||
      (outgoingAttachmentsEnabled && attachmentFile != null))

  const canSendVoice =
    outgoingAttachmentsEnabled &&
    messagesStatus === 'ready' &&
    !isSendBlocked &&
    !isSending &&
    voiceRecorderState.isVoiceReady

  const isComposerDisabled =
    messagesStatus !== 'ready' || isSending || isSendBlocked

  useEffect(() => {
    setIsVoiceRecordingMode(false)
    setVoiceRecorderState({
      isRecording: false,
      hasAudio: false,
      isVoiceReady: false,
    })
  }, [enabled, leadId, resolvedConversationId])

  useEffect(() => {
    if (outgoingAttachmentsEnabled) {
      return
    }
    setAttachmentFile(null)
    setIsVoiceRecordingMode(false)
    setVoiceRecorderState({
      isRecording: false,
      hasAudio: false,
      isVoiceReady: false,
    })
  }, [outgoingAttachmentsEnabled])

  const handleCloseVoiceMode = () => {
    setIsVoiceRecordingMode(false)
    setVoiceRecorderState({
      isRecording: false,
      hasAudio: false,
      isVoiceReady: false,
    })
  }

  const handleSendVoice = () => {
    if (!canSendVoice) {
      return
    }

    const file = voiceRecorderRef.current?.getVoiceFile()
    const nativePreviewUrl = voiceRecorderRef.current?.getPreviewUrl()
    if (!file || !nativePreviewUrl) {
      return
    }

    const durationSec = voiceRecorderRef.current?.getDurationSec()

    enableStickToBottom()
    setIsVoiceRecordingMode(false)
    setVoiceRecorderState({
      isRecording: false,
      hasAudio: false,
      isVoiceReady: false,
    })

    void (async () => {
      const previewBlob = await fetch(nativePreviewUrl).then((response) =>
        response.blob(),
      )
      const pendingPreviewUrl = URL.createObjectURL(previewBlob)

      try {
        await sendVoiceMessage(file, pendingPreviewUrl, durationSec)
        scrollToBottom()
      } catch {
        URL.revokeObjectURL(pendingPreviewUrl)
      }
    })()
  }
  const canMarkAllAsRead = messagesStatus === 'ready' && unreadCount > 0
  const isAllRead = messagesStatus === 'ready' && hasConversation && unreadCount === 0

  const handleSend = async () => {
    if (!canSend) {
      return
    }

    const text = draft.trim()
    const fileToSend = attachmentFile
    setDraft('')
    setAttachmentFile(null)
    enableStickToBottom()
    scrollToBottom()
    await sendMessage(text, fileToSend)
    scrollToBottom()
  }

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return
    }

    event.preventDefault()
    void handleSend()
  }

  const handleRetrySend = (clientId: string) => {
    enableStickToBottom()
    void retrySendMessage(clientId)
  }

  const handleVideoPlaybackError = () => {
    void loadMessages({ silent: true })
  }

  const handleVoicePlaybackError = () => {
    void loadMessages({ silent: true })
  }

  return (
    <div
      className={cn(classes.root, {
        [classes.rootDock]: variant === 'dock' || variant === 'page',
      })}
    >
      <header className={classes.header}>
        <div className={classes.headerMain}>
          {onBack ? (
            <button
              type="button"
              className={classes.backButton}
              aria-label="К списку чатов"
              onClick={onBack}
            >
              <ArrowLeftOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </button>
          ) : null}
          <div className={classes.avatar} aria-hidden>
            {getInitials(conversationTitle)}
          </div>
          <div className={classes.headerText}>
            <span className={classes.leadName}>{conversationTitle}</span>
            <span className={classes.leadMeta}>
              {provider === 'wazzup' ? (
                <WazzupSourceBadge type={wazzupChannelType} />
              ) : ProviderIcon ? (
                <span className={classes.vkBadge}>
                  <ProviderIcon className={classes.vkBadgeIcon} />
                  {providerMeta?.label ?? provider}
                </span>
              ) : null}
              <span className={classes.leadIdHint}>Лид #{leadId}</span>
            </span>
          </div>
        </div>
        <div className={classes.headerActions}>
          {canMarkAllAsRead ? (
            <Button
              type="button"
              color="secondary"
              className={classes.markReadButton}
              isLoading={isMarkingRead}
              disabled={isMarkingRead}
              aria-label={`Прочитать все, непрочитанных: ${unreadCount}`}
              onClick={() => void markDialogRead()}
            >
              <span className={classes.markReadButtonInner}>
                <span>Прочитать все</span>
                <span className={classes.markReadCountBadge}>
                  {formatUnreadBadgeCount(unreadCount)}
                </span>
              </span>
            </Button>
          ) : null}
          {isAllRead ? (
            <TooltipWrapper
              title={getAllReadBadgeTooltip(provider)}
              placement="bottomRight"
            >
              <span className={classes.allReadBadge} aria-live="polite">
                <CheckOutlined
                  className={classes.allReadIcon}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                Все прочитано
              </span>
            </TooltipWrapper>
          ) : null}
          {variant === 'dock' && onCollapse ? (
            <button
              type="button"
              className={classes.collapseButton}
              aria-label="Свернуть чат"
              onClick={onCollapse}
            >
              <CompressIcon />
            </button>
          ) : null}
          {variant !== 'dock' && variant !== 'page' && onDismiss ? (
            <button
              type="button"
              className={classes.closeButton}
              aria-label="Закрыть"
              onClick={onDismiss}
            >
              <IcoRemove />
            </button>
          ) : null}
        </div>
      </header>

      {resolvedTabs.length > 0 ? (
        <div
          className={classes.communityTabs}
          role="tablist"
          aria-label={getCrmIntegrationCommunityTabsAriaLabel(provider)}
        >
          {resolvedTabs.map((tab) => {
            const tabConversationId = Number(tab.conversation_id)
            const isActive = tabConversationId === Number(resolvedConversationId)
            const unreadLabel = formatVkUnreadCountBadge(Number(tab.unread_count ?? 0))
            const tabName = getCrmIntegrationCommunityTabLabel(tab, provider)

            return (
              <button
                key={tab.conversation_id}
                type="button"
                role="tab"
                aria-selected={isActive}
                title={
                  provider === 'wazzup' && tab.external_chat_type
                    ? formatWazzupChannelAriaLabel(tab.external_chat_type)
                    : undefined
                }
                className={cn(classes.communityTab, {
                  [classes.communityTabActive]: isActive,
                })}
                onClick={() => handleSelectConversation(tabConversationId)}
              >
                {provider === 'wazzup' && tab.external_chat_type ? (
                  <WazzupChannelTypeMark
                    type={tab.external_chat_type}
                    size="sm"
                    className={classes.communityTabChannelMark}
                  />
                ) : null}
                <span className={classes.communityTabName}>{tabName}</span>
                {unreadLabel ? (
                  <span className={classes.communityTabBadge} aria-hidden>
                    {unreadLabel}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      ) : null}

      <div
        ref={messagesViewportRef}
        className={classes.messagesViewport}
        onScroll={handleMessagesViewportScroll}
      >
        {messagesStatus === 'loading' && (
          <p className={classes.stateMessage}>Загрузка сообщений…</p>
        )}
        {messagesStatus === 'error' && (
          <p className={classes.stateMessageError}>
            {messagesErrorText ?? 'Не удалось загрузить сообщения'}
          </p>
        )}
        {messagesStatus === 'ready' && messages.length === 0 && (
          <p className={classes.stateMessage}>Сообщений пока нет</p>
        )}
        {messagesStatus === 'ready' && messages.length > 0 && (
          <div className={classes.messagesFeed}>
            {hasMoreOlder ? (
              <div className={classes.loadOlderRow}>
                <Button
                  type="button"
                  color="secondary"
                  className={classes.loadOlderButton}
                  isLoading={isLoadingOlder}
                  disabled={isLoadingOlder}
                  onClick={() => void loadOlderMessages()}
                >
                  Загрузить более ранние сообщения
                </Button>
              </div>
            ) : null}
            <CrmIntegrationTextChatMessageList
              ref={messagesListRef}
              messages={messages}
              provider={provider}
              wazzupChannelType={wazzupChannelType}
              onRetrySend={handleRetrySend}
              onDeleteMessage={deleteMessage}
              deletingMessageId={isDeletingMessageId}
              onVideoPlaybackError={handleVideoPlaybackError}
              onVoicePlaybackError={handleVoicePlaybackError}
            />
          </div>
        )}
      </div>

      <CrmIntegrationTextChatComposer
        compact={variant === 'dock' || variant === 'page'}
        disabled={isComposerDisabled}
        provider={provider}
        wazzupChannelType={wazzupChannelType}
        attachmentFile={attachmentFile}
        onAttachmentFileChange={setAttachmentFile}
        voiceMode={isVoiceRecordingMode}
        onOpenVoiceMode={() => setIsVoiceRecordingMode(true)}
        onCloseVoiceMode={handleCloseVoiceMode}
      >
        {isVoiceRecordingMode ? (
          <>
            <CrmIntegrationVoiceRecorder
              ref={voiceRecorderRef}
              disabled={isComposerDisabled}
              onRecordingStateChange={setVoiceRecorderState}
            />
            <Button
              type="button"
              color="primary"
              className={classes.sendButton}
              aria-label="Отправить голосовое сообщение"
              disabled={!canSendVoice}
              leftContent={
                <SendOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
              onClick={handleSendVoice}
            />
          </>
        ) : (
          <>
            <div className={classes.composerField}>
              {isSendBlocked ? (
                <p className={classes.composerBlockReason} role="status">
                  Отправка сообщений в этот диалог недоступна
                </p>
              ) : (
                <textarea
                  className={classes.composerInput}
                  placeholder="Сообщение..."
                  rows={variant === 'dock' || variant === 'page' ? 1 : 2}
                  value={draft}
                  disabled={isComposerDisabled}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                />
              )}
            </div>
            <Button
              type="button"
              color="primary"
              className={classes.sendButton}
              aria-label="Отправить сообщение"
              disabled={!canSend}
              leftContent={
                <SendOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
              onClick={() => void handleSend()}
            />
          </>
        )}
      </CrmIntegrationTextChatComposer>
    </div>
  )
}

export default CrmIntegrationTextChat
